import { useEffect, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter, useSearch } from '@tanstack/react-router';
import { alerts, users } from '@cwos/api-client';
import type { alerts as AlertsNS, users as UsersNS } from '@cwos/api-client';
import {
  ErrorPanel,
  FilteredEmpty,
  FilterSummary,
  NARROWING_UNKNOWN,
  NeedRow,
  Pill,
  Refused,
  SkeletonSet,
  SuccessEmpty,
  type NeedSeverity,
} from '@cwos/ui-admin';
import type { IconName } from '@cwos/ui-admin';
import type { Severity } from '@cwos/ui-shared';
import { useAuth } from '../../lib/auth.js';
import { useNav } from '../navigation.js';
import { gateOpen, type AuthorityGate } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { mayReadAlerts, mayReadIntegrationsHealth, usePermissions } from '../permissions.js';
import { useDataHealthCheck } from '../dataHealth.js';
import {
  ALERT_PAGE_SIZE,
  ALERT_STATUS_MEANING,
  ALERT_VERB_LABEL,
  QUEUE_STATUSES,
  alertRosterGate,
  alertStatusLabel,
  alertWriteGate,
  alertVerbsFor,
  attentionTrustCaveat,
  pageIsWorthAsking,
  partialNote,
  queueAnswer,
  queueScopeName,
  queueScopeNarrowedBy,
  settledNote,
  statusTotalOf,
  truncationNote,
  unknownStatusNote,
  verbFailure,
  verbFailureSentence,
  type AlertVerb,
  type StatusPageRead,
  type VerbFailure,
} from './attentionLogic.js';

type Alert = AlertsNS.Alert;

/**
 * The status, as a colour. `open` is `plain` on purpose — an untouched alert's
 * urgency is its SEVERITY, and colouring the status too would put two competing
 * temperature signals in one row.
 */
const STATUS_PILL: Record<string, Severity> = {
  open: 'plain',
  acknowledged: 'sky',
  assigned: 'sky',
  escalated: 'urgent',
  resolved: 'good',
};

/** One lifecycle write, as the API client wants it. */
type VerbInput =
  | { verb: 'acknowledge'; id: string }
  | { verb: 'assign'; id: string; userId: string }
  | { verb: 'resolve'; id: string; resolutionNotes: string }
  | { verb: 'escalate'; id: string; reason: string; newSeverity?: AlertsNS.AlertSeverity };

const NEED_SEVERITY: Record<string, NeedSeverity> = {
  critical: 'urgent',
  warning: 'look',
  info: 'unsure',
};
const NEED_ICON: Record<string, IconName> = {
  critical: 'urgent',
  warning: 'look',
  info: 'unsure',
};
const SEVERITIES = ['critical', 'warning', 'info'] as const;

/**
 * The triage queue.
 *
 * WHAT THE READ USED TO BE, in one line: `alerts.list({ limit: 100 })`. No
 * status, no severity, both applied in the browser afterwards — over a list the
 * server had ordered `created_at desc` across EVERY status, so settled alerts
 * consumed the hundred rows and the unresolved ones fell off the end. On top of
 * that the screen rendered `<SuccessEmpty>` when its own filtering left nothing,
 * which is how the product's strongest trust claim came to be printed over a
 * queue nobody had finished reading. `features/attention/attentionLogic.ts`
 * carries the full account.
 *
 * WHAT IT IS NOW. Four server-filtered reads, one per unresolved status, with
 * the chosen severity on each; the all-clear and every count come from the
 * server's own `total`, which the cap does not bind; and a read with a hole in
 * it renders its rows WITHOUT a total and without an all-clear, because the
 * missing read is exactly where the thing being missed would be.
 */
export function Attention() {
  const { me } = useAuth();
  const permissions = usePermissions();
  const { has, isLoading: permissionsLoading, isError: permissionsError } = permissions;
  /**
   * The same two keys `GET /api/alerts` checks. Below, the refusal was read off
   * the 403 — correct in what it rendered, and still one refused request every
   * 60 seconds for the whole time the screen is open. Deriving it from the
   * permission set means the read is never fired, and the answer is on screen
   * immediately rather than after a round trip.
   *
   * The 403 branch stays, because this set is cached for five minutes and a
   * permission revoked in that window is a real 403 arriving for a reader the
   * client still believes holds the keys.
   */
  const canReadAlerts = mayReadAlerts(has);
  const refusedByPermission = !permissionsLoading && !permissionsError && !canReadAlerts;
  /**
   * THE CROSS-CHECK BEHIND THE TRUST SENTENCE. `empty.successAttention` says
   * "you can trust it" and "we never hide anything" — a claim about DATA
   * HEALTH, not merely about this queue being empty. An empty alert queue and
   * a connector sitting on an incomplete historical import were previously
   * two facts nothing on this screen ever compared: `domains/alerts` never
   * once referenced `domains/integrations`. See `dataHealth.ts`.
   *
   * THREE-VALUED, NOT TWO (PH-ATT-001). 'unknown' — loading, a transient
   * failure, or a reader who cannot hold `integrations:read` — is NOT 'clear':
   * the trust sentence below may render its unqualified form only on 'clear'.
   */
  const dataHealthCheckResult = useDataHealthCheck(mayReadIntegrationsHealth(has));
  const navigate = useNav();
  const router = useRouter();
  const qc = useQueryClient();
  // `alert` is the deep link a notification opens (`/attention?alert=<id>`).
  const search = useSearch({ strict: false }) as { severity?: string; alert?: string };
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // A new link outranks an earlier click: the route does not remount when only
  // the search changes, so a bell item tapped while reading another alert
  // would otherwise leave that other alert open.
  useEffect(() => {
    setSelectedId(null);
  }, [search.alert]);

  const severity =
    search.severity && SEVERITIES.includes(search.severity as (typeof SEVERITIES)[number])
      ? search.severity
      : undefined;

  const setSeverity = (next?: string) => {
    void router.navigate({ to: '/attention', search: next ? { severity: next } : {} });
  };

  /**
   * How many pages deep the reader has asked to go, per status.
   *
   * Reset when the severity changes, because "show me more" is a statement
   * about a queue and choosing a severity makes it a different queue.
   */
  const [pages, setPages] = useState(1);
  /** Which alert's lifecycle write failed, which verb it was, and what the server said. */
  const [verbError, setVerbError] = useState<{
    id: string;
    verb: AlertVerb;
    failure: VerbFailure;
  } | null>(null);
  useEffect(() => {
    setPages(1);
  }, [severity]);

  const filters = severity ? { severity: severity as AlertsNS.AlertSeverity } : {};

  /**
   * PAGE 0 OF EACH UNRESOLVED STATUS.
   *
   * `listAlertsQuerySchema.status` takes ONE status (alerts/schemas.ts:82) and
   * "needs attention" is four of them, so this is four requests rather than one
   * — and that is the point: every row that comes back is unresolved, so a
   * month of resolved alerts cannot push an open one out of the page.
   *
   * Each response's `total` is `countAlertsByStatus` over the SAME filter and
   * is not bounded by `limit`, so these four reads also carry the uncapped
   * count this screen needs before it may say anything is clear.
   */
  const firstPages = useQueries({
    queries: QUEUE_STATUSES.map((status) => ({
      queryKey: ['alerts', 'queue', status, severity ?? 'any', 0],
      queryFn: () => alerts.list({ status, ...filters, limit: ALERT_PAGE_SIZE, offset: 0 }),
      refetchInterval: 60_000,
      enabled: canReadAlerts,
    })),
  });

  const firstReads: StatusPageRead[] = QUEUE_STATUSES.map((status, i) => ({
    status,
    page: 0,
    alerts: firstPages[i]?.data?.alerts,
    statusTotal: statusTotalOf(firstPages[i]?.data, status),
    isError: firstPages[i]?.isError ?? false,
    error: firstPages[i]?.error,
    organizationWide: firstPages[i]?.data?.organizationWide,
  }));

  /**
   * The pages the reader asked for beyond the first, and only where the
   * server's own count says there is something at that offset. A queue of six
   * alerts makes four requests however many times "Show more" is pressed.
   */
  const extra: { status: string; page: number }[] = [];
  for (let page = 1; page < pages; page += 1) {
    QUEUE_STATUSES.forEach((status, i) => {
      if (pageIsWorthAsking(page, firstReads[i]?.statusTotal)) extra.push({ status, page });
    });
  }

  const morePages = useQueries({
    queries: extra.map(({ status, page }) => ({
      queryKey: ['alerts', 'queue', status, severity ?? 'any', page],
      queryFn: () =>
        alerts.list({
          status: status as AlertsNS.AlertStatus,
          ...filters,
          limit: ALERT_PAGE_SIZE,
          offset: page * ALERT_PAGE_SIZE,
        }),
      refetchInterval: 60_000,
      enabled: canReadAlerts,
    })),
  });

  const moreReads: StatusPageRead[] = extra.map(({ status, page }, i) => ({
    status,
    page,
    alerts: morePages[i]?.data?.alerts,
    // Deliberately not summed: a total is a count over the filter, and every
    // page of a status repeats the same one. See `queueAnswer`.
    statusTotal: undefined,
    isError: morePages[i]?.isError ?? false,
    error: morePages[i]?.error,
    organizationWide: morePages[i]?.data?.organizationWide,
  }));

  const answer = queueAnswer([...firstReads, ...moreReads]);
  const rows: Alert[] = answer.kind === 'answered' || answer.kind === 'partial' ? answer.rows : [];
  /**
   * THE LINKED ALERT, WHEREVER IT IS. A notification usually outlives its
   * alert — data health closes its own within one sweep — so `?alert=<id>`
   * often names one this unresolved-only queue does not hold. Then it is read
   * by id (same visibility as the list) and shown with its own state, never
   * swapped for the first row as though that were the one linked.
   */
  const linkedId = selectedId === null ? (search.alert ?? null) : null;
  const linkedRow = linkedId === null ? undefined : rows.find((a) => a.id === linkedId);
  const linkedOutside = linkedId !== null && linkedRow === undefined;
  const showLinked =
    canReadAlerts && linkedOutside && answer.kind !== 'loading' && answer.kind !== 'refused';
  const linked = useQuery({
    queryKey: ['alerts', 'detail', linkedId],
    queryFn: () => alerts.detail(linkedId as string),
    enabled: showLinked,
  });
  const selected = linkedOutside
    ? undefined
    : (rows.find((a) => a.id === (selectedId ?? linkedId)) ?? rows[0]);
  // PH-ATT-003. `undefined` while `answer.kind` is anything but 'answered' —
  // the scope note below already has its own reasons (partial, loading,
  // refused, failed) to withhold a frame in those states, via `narrowedBy`.
  const organizationWide = answer.kind === 'answered' ? answer.organizationWide : undefined;
  const trustCaveat = attentionTrustCaveat(dataHealthCheckResult, organizationWide);

  /**
   * MAY THIS READER ACT, AND IF NOT, WHY NOT.
   *
   * `authorityGateAll`, not `has('alerts:write')`. A bare `has` collapses "not
   * held", "not arrived yet" and "could not be read" into one `false`, and a
   * screen that renders a refusal from the second or third accuses somebody of
   * lacking access they may well hold — the defect
   * `docs/intelligence/audit/UNKNOWN-IS-NOT-DENIED-FRONTEND.md` catalogues at
   * seven sites. `All` because the route charges two keys and answers 403
   * naming whichever is missing.
   */
  const writeGate = alertWriteGate({
    has,
    isLoading: permissionsLoading,
    isError: permissionsError,
  });
  /**
   * `GET /users` charges `user:read`, which `alerts:write` does not imply —
   * `supervisor` holds the second and not necessarily the first. So the roster
   * behind "assign to somebody else" has its OWN gate, and the pane offers
   * "assign to me" either way: a supervisor who may work the queue can always
   * take an item, and the picker is what needs the extra key.
   */
  const rosterGate = alertRosterGate({
    has,
    isLoading: permissionsLoading,
    isError: permissionsError,
  });

  /**
   * ASKED FOR ONLY WHEN SOMEBODY OPENS THE ASSIGN FORM.
   *
   * `GET /users` returns every account in the tenant. Firing it on every visit
   * to a screen whose ordinary use is "read the queue, acknowledge one thing"
   * is a whole-workforce read nobody asked for, and this screen already refuses
   * to make reads it does not need.
   */
  const [rosterWanted, setRosterWanted] = useState(false);
  const roster = useQuery({
    queryKey: ['users', 'alert-assignees'],
    queryFn: () => users.list(),
    enabled: rosterWanted && gateOpen(rosterGate),
    staleTime: 300_000,
  });

  /**
   * THE FOUR LIFECYCLE WRITES, AND WHAT HAPPENS WHEN ONE DOES NOT SAVE.
   *
   * The screen used to carry `acknowledge` alone; `assign`, `resolve` and
   * `escalate` shipped in the API client with zero call sites, so nothing in
   * the product could take an alert out of the attention queue and the header
   * badge could only grow. See `attentionLogic.ts`.
   *
   * A 409 IS HANDLED AS AN ANSWER, NOT A FAILURE. The service's lifecycle
   * refusals mean the row moved between the read and the write, which on a
   * shared queue is ordinary — so the queue is re-read and the pane says the
   * alert changed, rather than offering a retry that would repeat the refusal.
   */
  const act = useMutation({
    mutationFn: (input: VerbInput) => {
      switch (input.verb) {
        case 'acknowledge':
          return alerts.acknowledge(input.id);
        case 'assign':
          return alerts.assign(input.id, input.userId);
        case 'resolve':
          return alerts.resolve(input.id, input.resolutionNotes);
        case 'escalate':
          return alerts.escalate(input.id, input.reason, input.newSeverity);
      }
    },
    onMutate: () => {
      setVerbError(null);
    },
    onSuccess: () => {
      setVerbError(null);
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: (err: unknown, input: VerbInput) => {
      // Keyed by alert id AND verb: a queue shows many, and a floating "that
      // didn't work" names neither the alert nor what was attempted.
      const failure = verbFailure(err);
      setVerbError({ id: input.id, verb: input.verb, failure });
      // The row moved under us. Re-read so the pane's offered verbs are the
      // ones the alert's NEW status admits.
      if (failure.stale) void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const retry = () => {
    for (const q of firstPages) void q.refetch();
    for (const q of morePages) void q.refetch();
  };

  const scopeNarrowing = queueScopeNarrowedBy(organizationWide);

  const scope = {
    what: 'items that need you',
    // READ OFF THE SERVER'S OWN `organizationWide` FACT, never re-derived from
    // `me`'s permissions — a caseload-scoped reader's queue is no longer
    // labelled with the organization's name (PH-ATT-003; mirrors
    // `Insights.tsx`'s identical `frameSentence` choice for the same reason).
    scope: queueScopeName(organizationWide, me ? (me.orgName ?? me.orgId) : 'your organization'),
    period: 'right now',
    narrowings: [
      ...(severity ? [{ label: severity }] : []),
      // Two narrowings this screen applies. Both are now the SERVER's — the
      // status filter and the page cap are on the request, not on the browser's
      // copy of it — and both are still narrowings the reader did not type, so
      // the completeness promise still may not be made without them.
      { label: 'unresolved only' },
      ...(answer.kind === 'answered' && answer.more
        ? [{ label: `first ${answer.rows.length}` }]
        : []),
    ],
    // A queue with a read missing is not a queue anybody can frame. Neither the
    // promise nor its denial: `NARROWING_UNKNOWN` is the third value and this is
    // what it is for. An answered read whose SCOPE itself came back unknown
    // (should not happen once every response carries `organizationWide`, but
    // an old cached response could) gets the same treatment.
    narrowedBy:
      answer.kind === 'partial' || scopeNarrowing === 'unknown'
        ? NARROWING_UNKNOWN
        : scopeNarrowing === 'narrowed'
          ? 'the people you supervise'
          : undefined,
  };

  const pane = (alert: Alert) => (
    <AlertDetailPane
      // Keyed by alert id so the half-typed resolution note for one alert
      // never appears under another.
      key={alert.id}
      alert={alert}
      writeGate={writeGate}
      rosterGate={rosterGate}
      roster={roster.data}
      rosterFailed={roster.isError}
      onNeedRoster={() => setRosterWanted(true)}
      meUserId={me?.userId ?? null}
      busyVerb={act.isPending && act.variables?.id === alert.id ? act.variables.verb : null}
      failure={verbError?.id === alert.id ? verbError : null}
      onAct={(input) => act.mutate(input)}
      onRetryPermissions={permissions.refetch}
      onOpenSubject={(href) => navigate(href)}
    />
  );

  return (
    <div>
      <h1 className="rn-card-title" style={{ marginBottom: 'var(--space-12)' }}>
        Needs attention
      </h1>

      <FilterSummary scope={scope} />

      {showLinked ? (
        <div className="rn-card" data-attention-linked style={{ marginBottom: 'var(--space-12)' }}>
          <p className="rn-muted" style={{ fontSize: 13, marginTop: 0 }}>
            The item you followed is not in this list of open items.
          </p>
          {linked.data ? (
            pane(linked.data)
          ) : linked.isError ? (
            <p className="tc-warn" role="alert">
              We couldn’t open the item you followed. It may no longer exist, or it is not visible
              to you.
            </p>
          ) : (
            <p className="rn-muted" role="status">
              Opening the item you followed…
            </p>
          )}
        </div>
      ) : null}

      {/* One choice, four options — a segmented control, which is what
          `.rn-tabs` already is everywhere else in the app. These were four
          `.rn-link` buttons in a card of their own: link-blue, evenly spaced,
          and identical whether pressed or not except for a colour the pressed
          state did not actually change. Nothing on the screen said which
          severity was showing. The `aria-pressed` contract is unchanged — it is
          the same four buttons, and `.rn-tab` styles that state. */}
      <div className="rn-tabs" role="group" aria-label="Filter by severity">
        <button
          type="button"
          className="rn-tab"
          onClick={() => setSeverity(undefined)}
          aria-pressed={!severity}
        >
          All
        </button>
        {SEVERITIES.map((s) => (
          <button
            key={s}
            type="button"
            className="rn-tab"
            onClick={() => setSeverity(s)}
            aria-pressed={severity === s}
          >
            {s}
          </button>
        ))}
      </div>

      {/* `GET /api/alerts` 403s a caller holding `alerts:read` without
          `directory:read`, and that refusal was landing in the failure branch —
          a retry button on an answer, above an "all clear" this screen must
          never fabricate. */}
      {refusedByPermission || answer.kind === 'refused' ? (
        <Refused
          heading="Attention items are not shown to you"
          message="Reading alerts also needs permission to view people in your organization. Nothing is being hidden from the list — the list is not yours to see."
        />
      ) : permissionsLoading || answer.kind === 'loading' ? (
        <SkeletonSet surface="table" rows={6} />
      ) : answer.kind === 'failed' ? (
        <ErrorPanel
          message="We couldn’t load attention items just now. Do not read this as nothing needing you."
          onRetry={retry}
        />
      ) : answer.kind === 'answered' && answer.total === 0 ? (
        // The all-clear, and now it is the server's statement rather than the
        // page's. `total` is counted over every matching row, so an empty one is
        // an empty QUEUE and not an empty first hundred.
        //
        // With a severity chosen it is a statement about that severity only, so
        // it is the filtered empty and not the trust claim: "no critical items"
        // is not "nothing is being missed".
        severity ? (
          <FilteredEmpty onClear={() => setSeverity(undefined)} />
        ) : (
          // THE TWO CLAUSES THIS MUST NOT SAY WHILE EITHER CROSS-CHECK HAS NOT
          // COME BACK FAVOURABLE: "you can trust it" and "we never hide
          // anything to keep things looking tidy" — full trust, not merely an
          // empty queue. TWO INDEPENDENT REASONS can withhold it now:
          //   - PH-ATT-001: the data-health check is 'gap' or 'unknown'
          //     (a connected integration contradicts it, or the check itself
          //     did not come back — this used to FAIL OPEN on 'unknown').
          //   - PH-ATT-003: `organizationWide` is `false` or `undefined` — a
          //     caseload-scoped queue, or one whose scope this read did not
          //     state, cannot vouch for the organization it is not labelled
          //     with above.
          // Either way the default two-clause sentence is swapped for an
          // honest one rather than asserting completeness it cannot back.
          <SuccessEmpty
            message={trustCaveat ? `No open items right now — but ${trustCaveat}` : undefined}
          />
        )
      ) : (
        <>
          {answer.kind === 'partial' ? (
            <div style={{ marginBottom: 'var(--space-12)' }}>
              <ErrorPanel message={partialNote(answer.missing)} onRetry={retry} />
            </div>
          ) : null}

          {answer.kind === 'answered' && answer.more ? (
            <p className="rn-muted" role="status" style={{ fontSize: 13 }}>
              {truncationNote(answer.rows.length, answer.total)}
            </p>
          ) : null}

          {rows.length === 0 ? (
            // Answered, counted above zero, and nothing came back — the statuses
            // moved under the read. Not an all-clear, and not silence either.
            <ErrorPanel
              message="The queue is not empty, but none of its items came back on this read. Ask again rather than reading this as nothing needing you."
              onRetry={retry}
            />
          ) : (
            <div className="split">
              <div className="split-list rn-card">
                {rows.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="rn-link"
                    style={{ textAlign: 'left', width: '100%' }}
                    onClick={() => setSelectedId(a.id)}
                  >
                    <NeedRow
                      severity={NEED_SEVERITY[a.severity] ?? 'unsure'}
                      icon={NEED_ICON[a.severity] ?? 'unsure'}
                      title={a.title}
                      message={a.description}
                      // NOT `acknowledged={a.status === 'acknowledged'}`. Four
                      // unresolved statuses reached this row and three of them
                      // drew identically: an alert somebody had been ASSIGNED,
                      // and one the rules engine had ESCALATED, looked exactly
                      // like one nobody had touched. The pill names the status
                      // the server actually returned.
                      pill={{
                        variant: STATUS_PILL[a.status] ?? 'unsure',
                        label: alertStatusLabel(a.status),
                      }}
                    />
                  </button>
                ))}
                {answer.kind === 'answered' && answer.more ? (
                  <button
                    type="button"
                    className="rn-btn-quiet"
                    style={{ width: '100%' }}
                    onClick={() => setPages((p) => p + 1)}
                  >
                    Show more
                  </button>
                ) : null}
              </div>

              <div className="rn-card">
                {selected ? (
                  pane(selected)
                ) : (
                  <p className="rn-muted" style={{ fontSize: 13 }}>
                    Choose an item to see it here.
                  </p>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * THE ALERT, AND EVERY VERB THE SERVER WILL ACCEPT FOR IT.
 *
 * The pane used to carry one button. `alerts.assign`, `alerts.resolve` and
 * `alerts.escalate` existed in `@cwos/api-client` and were called from nowhere,
 * so an alert could be acknowledged and then never left the queue.
 *
 * WHICH BUTTONS APPEAR IS THE SERVER'S ANSWER, not this file's opinion:
 * `alertVerbsFor(status)` transcribes `alerts/service.ts`'s own refusals, so a
 * verb is offered exactly when the service would accept it. An `open` alert
 * shows "Got it" and "Escalate" and NOT "Resolve", because `resolveAlert`
 * answers 409 to an unacknowledged alert; an `escalated` one shows assign,
 * resolve and escalate-again.
 *
 * THE THREE NEW VERBS ALL TAKE SOMETHING, and each one is required by the
 * server's zod schema rather than by taste — `resolutionNotes` (1..2000
 * characters), an escalation `reason`, an assignee that must be an ACTIVE user
 * in the tenant. So each opens a small form and the submit stays disabled until
 * it can succeed. A button that posts an empty body to earn a 400 is a control
 * the server will refuse, which is the thing this pane was asked not to build.
 */
function AlertDetailPane({
  alert,
  writeGate,
  rosterGate,
  roster,
  rosterFailed,
  onNeedRoster,
  meUserId,
  busyVerb,
  failure,
  onAct,
  onRetryPermissions,
  onOpenSubject,
}: {
  alert: Alert;
  /** `alerts:write` + `directory:read`, four-state. */
  writeGate: AuthorityGate;
  /** `user:read` — the roster behind "assign to somebody else", four-state. */
  rosterGate: AuthorityGate;
  roster: UsersNS.Person[] | undefined;
  rosterFailed: boolean;
  /** Raised when the assign form opens; the parent reads `/users` only then. */
  onNeedRoster: () => void;
  meUserId: string | null;
  /** The verb currently in flight for THIS alert, or null. */
  busyVerb: AlertVerb | null;
  /** The verb that failed on this alert and what the server said, or null. */
  failure: { verb: AlertVerb; failure: VerbFailure } | null;
  onAct: (input: VerbInput) => void;
  onRetryPermissions: () => void;
  onOpenSubject: (href: string) => void;
}) {
  const [openForm, setOpenForm] = useState<AlertVerb | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [reason, setReason] = useState('');
  const [newSeverity, setNewSeverity] = useState<'' | AlertsNS.AlertSeverity>('');
  const [assignee, setAssignee] = useState('');

  const variant =
    alert.severity === 'critical' ? 'urgent' : alert.severity === 'warning' ? 'look' : 'unsure';
  const verbs = alertVerbsFor(alert.status);
  const settled = settledNote(alert.status);
  const unknown = unknownStatusNote(alert.status);

  /**
   * Only ACTIVE accounts. `assignAlert` verifies the id names an `app_user`
   * with `status = 'active'` and 404s otherwise — the invited-but-not-activated
   * case is exactly the one the service's own docblock says used to leave an
   * alert assigned to somebody who could not sign in.
   */
  const assignable = (roster ?? []).filter((p) => p.status === 'active');

  const submit = (input: VerbInput) => {
    onAct(input);
    setOpenForm(null);
  };

  return (
    <div>
      <div
        style={{
          marginBottom: 'var(--space-10)',
          display: 'flex',
          gap: 'var(--space-8)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Pill variant={variant} dot>
          {alert.severity}
        </Pill>
        {/* THE STATUS, NAMED. `escalated` had no label anywhere in the admin
            app — a status the rules engine really does write, and the strongest
            signal this queue produces, drawn as nothing. */}
        <Pill variant={STATUS_PILL[alert.status] ?? 'unsure'}>
          {alertStatusLabel(alert.status)}
        </Pill>
      </div>
      <h2 className="rn-card-title" style={{ marginBottom: 'var(--space-8)' }}>
        {alert.title}
      </h2>
      <p style={{ color: 'var(--color-ink2)', marginTop: 0 }}>{alert.description}</p>
      {ALERT_STATUS_MEANING[alert.status] ? (
        <p className="rn-muted" style={{ fontSize: 13, marginTop: 0 }}>
          {ALERT_STATUS_MEANING[alert.status]}
        </p>
      ) : null}
      {alert.resolutionNotes ? (
        <p style={{ fontSize: 13 }} data-alert-resolution>
          Resolution: {alert.resolutionNotes}
        </p>
      ) : null}

      {alert.sourceEntityType && alert.sourceEntityId ? (
        <button
          type="button"
          className="rn-link"
          onClick={() =>
            // The server writes exactly one sourceEntityType today ('worker'),
            // whose subject page is real; anything it mints later lands on the
            // Monitor queue rather than the dead /delivery?evidence= URL this
            // used to build (no such route; nothing consumes the parameter).
            onOpenSubject(
              alert.sourceEntityType === 'worker'
                ? `/people/${alert.sourceEntityId}`
                : '/dashboard?view=monitor',
            )
          }
        >
          {alert.sourceEntityType} →
        </button>
      ) : null}

      <div style={{ marginTop: 'var(--space-16)' }}>
        {settled !== null ? (
          <p className="rn-muted" role="status" data-alert-settled>
            {settled}
          </p>
        ) : unknown !== null ? (
          <p className="rn-muted" role="status" data-alert-unknown-status>
            {unknown}
          </p>
        ) : (
          <>
            {/* Unresolved, unknown or refused — all three said once, here, and
                never as "you may not" from a set that has not arrived. */}
            <GateNotice
              gate={writeGate}
              what="act on alerts"
              heading="Acting on alerts is not yours to do"
              detail="Acknowledging, assigning, resolving and escalating an alert all need permission to write alerts and to view people in your organization. The queue is still yours to read."
              onRetry={onRetryPermissions}
            />

            {gateOpen(writeGate) ? (
              <>
                <div
                  className="rn-tabs"
                  role="group"
                  aria-label="Actions"
                  style={{ marginBottom: 'var(--space-10)' }}
                >
                  {verbs.map((verb) => (
                    <button
                      key={verb}
                      type="button"
                      className="rn-tab"
                      data-alert-verb={verb}
                      aria-pressed={openForm === verb}
                      disabled={busyVerb !== null}
                      onClick={() => {
                        // `acknowledge` takes nothing the server requires, so
                        // it fires straight away rather than opening an empty
                        // form. The other three all have a required field.
                        if (verb === 'acknowledge') {
                          submit({ verb, id: alert.id });
                          return;
                        }
                        if (verb === 'assign') onNeedRoster();
                        setOpenForm(openForm === verb ? null : verb);
                      }}
                    >
                      {busyVerb === verb
                        ? 'Saving…'
                        : // A FAILED WRITE MUST NOT LEAVE THE BUTTON LOOKING
                          // UNTOUCHED — that is what made the old silent
                          // acknowledgement failure invisible. A 409 gets no
                          // "try again": the alert moved, the queue is being
                          // re-read, and pressing again repeats the answer.
                          failure?.verb === verb && !failure.failure.stale
                          ? 'Try again'
                          : ALERT_VERB_LABEL[verb]}
                    </button>
                  ))}
                </div>

                {openForm === 'assign' ? (
                  <div data-alert-form="assign">
                    {/* ALWAYS AVAILABLE. Taking an item is the common triage
                        act and needs no roster: the id is the reader's own. */}
                    {meUserId ? (
                      <button
                        type="button"
                        className="rn-btn-quiet"
                        data-alert-assign-me
                        disabled={busyVerb !== null}
                        onClick={() => submit({ verb: 'assign', id: alert.id, userId: meUserId })}
                      >
                        Assign to me
                      </button>
                    ) : null}

                    {/* AND THE ROSTER, WHICH IS A SECOND PERMISSION. Refused,
                        unresolved and unreadable are three different answers
                        and `GateNotice` says each of them in its own words. */}
                    <GateNotice
                      gate={rosterGate}
                      what="choose somebody else to assign this to"
                      heading="Assigning to somebody else is not shown to you"
                      detail="Picking a person needs permission to read the user list. You can still take this alert yourself."
                      onRetry={onRetryPermissions}
                    />
                    {gateOpen(rosterGate) ? (
                      rosterFailed ? (
                        <p className="tc-warn" role="alert" style={{ fontSize: 13 }}>
                          We couldn’t read the list of people just now, so there is nobody to choose
                          from. This is a connection problem, not an empty organization.
                        </p>
                      ) : roster === undefined ? (
                        <p className="rn-muted" role="status" style={{ fontSize: 13 }}>
                          Loading people…
                        </p>
                      ) : (
                        <div style={{ marginTop: 'var(--space-8)' }}>
                          <label
                            className="rn-muted"
                            style={{ display: 'block', fontSize: 13 }}
                            htmlFor="alert-assignee"
                          >
                            Assign to
                          </label>
                          <select
                            id="alert-assignee"
                            value={assignee}
                            onChange={(e) => setAssignee(e.target.value)}
                          >
                            <option value="">Choose a person…</option>
                            {assignable.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="signin-devbtn"
                            style={{
                              width: 'auto',
                              padding: 'var(--space-8) var(--space-16)',
                              marginLeft: 'var(--space-8)',
                            }}
                            disabled={assignee === '' || busyVerb !== null}
                            onClick={() =>
                              submit({ verb: 'assign', id: alert.id, userId: assignee })
                            }
                          >
                            Assign
                          </button>
                        </div>
                      )
                    ) : null}
                  </div>
                ) : null}

                {openForm === 'resolve' ? (
                  <div data-alert-form="resolve">
                    <label
                      className="rn-muted"
                      style={{ display: 'block', fontSize: 13 }}
                      htmlFor="alert-resolution"
                    >
                      What was done? This becomes part of the alert’s permanent record and cannot be
                      edited afterwards.
                    </label>
                    <textarea
                      id="alert-resolution"
                      rows={3}
                      style={{ width: '100%' }}
                      maxLength={2000}
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                    />
                    <button
                      type="button"
                      className="signin-devbtn"
                      style={{ width: 'auto', padding: 'var(--space-8) var(--space-16)' }}
                      // `resolveAlertSchema` requires 1..2000 trimmed
                      // characters. Enabling this on an empty box would post a
                      // body the server answers 400 to.
                      disabled={resolutionNotes.trim() === '' || busyVerb !== null}
                      onClick={() =>
                        submit({
                          verb: 'resolve',
                          id: alert.id,
                          resolutionNotes: resolutionNotes.trim(),
                        })
                      }
                    >
                      Resolve
                    </button>
                  </div>
                ) : null}

                {openForm === 'escalate' ? (
                  <div data-alert-form="escalate">
                    <label
                      className="rn-muted"
                      style={{ display: 'block', fontSize: 13 }}
                      htmlFor="alert-escalation-reason"
                    >
                      Why is this being escalated?
                    </label>
                    <textarea
                      id="alert-escalation-reason"
                      rows={2}
                      style={{ width: '100%' }}
                      maxLength={2000}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <label
                      className="rn-muted"
                      style={{ display: 'block', fontSize: 13 }}
                      htmlFor="alert-new-severity"
                    >
                      Severity
                    </label>
                    <select
                      id="alert-new-severity"
                      value={newSeverity}
                      onChange={(e) =>
                        setNewSeverity(e.target.value as '' | AlertsNS.AlertSeverity)
                      }
                    >
                      {/* The server raises severity one level when none is
                          given (`escalateAlert`), so "leave it to the rule" is
                          a real option and is the default rather than a blank
                          the caller has to fill in. */}
                      <option value="">Raise it one level</option>
                      {SEVERITIES.map((sv) => (
                        <option key={sv} value={sv}>
                          {sv}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="signin-devbtn"
                      style={{
                        width: 'auto',
                        padding: 'var(--space-8) var(--space-16)',
                        marginLeft: 'var(--space-8)',
                      }}
                      disabled={reason.trim() === '' || busyVerb !== null}
                      onClick={() =>
                        submit({
                          verb: 'escalate',
                          id: alert.id,
                          reason: reason.trim(),
                          ...(newSeverity === '' ? {} : { newSeverity }),
                        })
                      }
                    >
                      Escalate
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        )}

        {failure !== null ? (
          <p
            className="tc-warn"
            role="alert"
            data-alert-failure={failure.failure.stale ? 'stale' : 'failed'}
            style={{ marginTop: 'var(--space-8)' }}
          >
            {verbFailureSentence(failure.verb, failure.failure)}
          </p>
        ) : null}
      </div>
    </div>
  );
}
